import { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import type { HelpArticle } from "../lib/types";
import { fetchAllArticlesForAdmin, archiveArticle, restoreArticle } from "../lib/admin-data";
import { useAuth } from "../components/AuthContext";
import { signOut } from "../lib/auth";
import { SEO } from "../components/SEO";

type FilterType = "all" | "published" | "draft" | "archived";

export function AdminListPage() {
  const { session } = useAuth();
  const [articles, setArticles] = useState<HelpArticle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<FilterType>("all");

  useEffect(() => {
    load();
  }, []);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchAllArticlesForAdmin();
      setArticles(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unknown error");
    }
    setLoading(false);
  }

  const filtered = useMemo(() => {
    let result = articles;
    if (filter === "published") result = result.filter((a) => a.is_published && !a.archived_at);
    else if (filter === "draft") result = result.filter((a) => !a.is_published && !a.archived_at);
    else if (filter === "archived") result = result.filter((a) => a.archived_at);

    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (a) => a.title.toLowerCase().includes(q) || a.slug.toLowerCase().includes(q)
      );
    }
    return result;
  }, [articles, filter, search]);

  async function handleArchive(id: string) {
    if (!confirm("Archive this article? It will no longer be publicly visible.")) return;
    try {
      await archiveArticle(id);
      await load();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Failed to archive");
    }
  }

  async function handleRestore(id: string) {
    try {
      await restoreArticle(id);
      await load();
    } catch (e) {
      alert(e instanceof Error ? e.message : "Failed to restore");
    }
  }

  async function handleSignOut() {
    await signOut();
    window.location.href = "/";
  }

  if (loading) {
    return (
      <div className="loading-state">
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <>
      <SEO
        title="Admin Dashboard — TrustReq Help Centre"
        description="Manage help centre articles."
        canonical="https://help.trustreq.ca/admin/help"
      />
      <div className="admin-page">
        <div className="admin-header">
          <div className="admin-header-left">
            <h1 className="admin-title">Help Centre Admin</h1>
            <span className="admin-user">{session?.user?.email}</span>
          </div>
          <div className="admin-header-right">
            <Link to="/admin/help/new" className="btn btn-primary">+ New Article</Link>
            <a href="/" className="btn btn-secondary">View Site</a>
            <button onClick={handleSignOut} className="btn btn-secondary">Sign Out</button>
          </div>
        </div>

        {error && <div className="admin-error">{error}</div>}

        <div className="admin-toolbar">
          <input
            type="search"
            className="form-input admin-search"
            placeholder="Search by title or slug..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="admin-filters">
            {(["all", "published", "draft", "archived"] as FilterType[]).map((f) => (
              <button
                key={f}
                className={`filter-btn ${filter === f ? "active" : ""}`}
                onClick={() => setFilter(f)}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>

        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Slug</th>
                <th>Type</th>
                <th>Order</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => (
                <tr key={a.id} className={a.archived_at ? "row-archived" : ""}>
                  <td className="cell-title">
                    <Link to={`/admin/help/edit/${a.id}`}>{a.title}</Link>
                  </td>
                  <td className="cell-slug">{a.slug}</td>
                  <td className="cell-type">{a.page_type}</td>
                  <td className="cell-order">{a.nav_order}</td>
                  <td className="cell-status">
                    {a.archived_at ? (
                      <span className="badge badge-archived">Archived</span>
                    ) : a.is_published ? (
                      <span className="badge badge-published">Published</span>
                    ) : (
                      <span className="badge badge-draft">Draft</span>
                    )}
                  </td>
                  <td className="cell-actions">
                    <Link to={`/admin/help/edit/${a.id}`} className="btn btn-sm btn-secondary">Edit</Link>
                    <Link to={`/admin/help/revisions/${a.id}`} className="btn btn-sm btn-secondary">Revisions</Link>
                    {a.archived_at ? (
                      <button onClick={() => handleRestore(a.id)} className="btn btn-sm btn-primary">Restore</button>
                    ) : (
                      <button onClick={() => handleArchive(a.id)} className="btn btn-sm btn-danger">Archive</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {filtered.length === 0 && (
            <div className="admin-empty">No articles match the current filter.</div>
          )}
        </div>
      </div>
    </>
  );
}
